import { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '@/lib/backendUrl';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { DollarSign, Shield, FileText, ArrowDownToLine, RefreshCw, Lock, TrendingUp, Link2, CheckCircle2, AlertTriangle, ExternalLink } from 'lucide-react';

export function CryptoLedgerTab({ hotelId }) {
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [settlement, setSettlement] = useState(null);
  const [seal, setSeal] = useState(null);
  const [verification, setVerification] = useState(null);
  const [taxInfo, setTaxInfo] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [availableMonths, setAvailableMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settlementLoading, setSettlementLoading] = useState(false);
  const [sealLoading, setSealLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, taxRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/crypto-ledger/${hotelId}/summary`),
        fetch(`${BACKEND_URL}/api/crypto-ledger/${hotelId}/tax-info`),
      ]);
      const summaryData = await summaryRes.json();
      const taxData = await taxRes.json();

      if (summaryData.success) {
        setSummary(summaryData);
        setAvailableMonths(summaryData.available_months || []);
        if (!selectedMonth && summaryData.available_months?.length > 0) {
          setSelectedMonth(summaryData.available_months[0]);
        }
      }
      if (taxData.success) setTaxInfo(taxData);
    } catch (err) {
      toast.error('Failed to load crypto ledger');
    } finally {
      setLoading(false);
    }
  }, [hotelId, selectedMonth]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!selectedMonth) return;
    const fetchEntries = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/crypto-ledger/${hotelId}/entries?month=${selectedMonth}&limit=100`);
        const data = await res.json();
        if (data.success) setEntries(data.entries || []);
      } catch { /* silent */ }
    };
    fetchEntries();
  }, [hotelId, selectedMonth]);

  const loadSettlement = async (month) => {
    if (!month) return;
    setSettlementLoading(true);
    setSeal(null);
    setVerification(null);
    try {
      const [year, m] = month.split('-');
      const [settleRes, sealRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/crypto-ledger/${hotelId}/settlement/${year}/${parseInt(m)}`),
        fetch(`${BACKEND_URL}/api/crypto-ledger/${hotelId}/verify-seal/${year}/${parseInt(m)}`),
      ]);
      const settleData = await settleRes.json();
      const sealData = await sealRes.json();
      if (settleData.success) setSettlement(settleData);
      if (sealData.success && sealData.has_seal) {
        setSeal(sealData.seal);
        setVerification(sealData);
      }
    } catch {
      toast.error('Failed to load settlement report');
    } finally {
      setSettlementLoading(false);
    }
  };

  const handleSealOnChain = async () => {
    if (!selectedMonth) return;
    setSealLoading(true);
    try {
      const [year, m] = selectedMonth.split('-');
      const res = await fetch(`${BACKEND_URL}/api/crypto-ledger/${hotelId}/seal/${year}/${parseInt(m)}`, { method: 'POST' });
      const data = await res.json();
      if (data.success && data.sealed) {
        setSeal(data.seal);
        setVerification({ verified: true, has_seal: true, seal: data.seal });
        if (data.on_chain) {
          toast.success('Settlement sealed on Base Sepolia!');
        } else {
          toast.success('Settlement hash recorded (off-chain fallback)');
        }
      } else {
        toast.error(data.detail || data.error || 'Seal failed');
      }
    } catch {
      toast.error('Failed to seal settlement on-chain');
    } finally {
      setSealLoading(false);
    }
  };

  const handleBackfill = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/crypto-ledger/${hotelId}/backfill`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        toast.success(`Backfilled ${data.backfilled} existing crypto bookings`);
        fetchData();
      }
    } catch {
      toast.error('Backfill failed');
    }
  };

  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);
  const fmtPct = (n) => `${((n || 0) * 100).toFixed(1)}%`;

  if (loading) {
    return (
      <div className="space-y-4" data-testid="crypto-ledger-loading">
        <div className="h-8 bg-muted animate-pulse rounded w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-28 bg-muted animate-pulse rounded-lg" />)}
        </div>
      </div>
    );
  }

  const allTime = summary?.all_time || {};
  const currentMonth = summary?.current_month || {};

  return (
    <div className="space-y-6" data-testid="crypto-ledger-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold heading-font">Crypto Payment Ledger</h2>
          <p className="text-muted-foreground text-sm">
            Fiat-locked values for tax compliance. Every crypto payment is recorded at the USD rate at the moment of payment.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleBackfill} data-testid="backfill-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Backfill Existing
          </Button>
        </div>
      </div>

      {/* Tax Jurisdiction Banner */}
      {taxInfo && (
        <div className="bg-muted/50 border rounded-lg px-4 py-3 flex items-center gap-3" data-testid="tax-jurisdiction-info">
          <Shield className="h-5 w-5 text-primary shrink-0" />
          <div className="text-sm">
            <span className="font-medium">{taxInfo.tax_label}</span>
            <span className="text-muted-foreground ml-2">{fmtPct(taxInfo.tax_rate)} — {taxInfo.tax_notes}</span>
            <span className="text-muted-foreground ml-2">({taxInfo.city}, {taxInfo.country})</span>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="all-time-revenue-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <DollarSign className="h-4 w-4" /> All-Time Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{fmt(allTime.total_fiat_revenue)}</div>
            <p className="text-xs text-muted-foreground">{allTime.total_payments || 0} crypto payments</p>
          </CardContent>
        </Card>

        <Card data-testid="all-time-tax-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Lock className="h-4 w-4" /> Tax Set Aside
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{fmt(allTime.total_tax_collected)}</div>
            <p className="text-xs text-muted-foreground">Locked at payment time</p>
          </CardContent>
        </Card>

        <Card data-testid="all-time-net-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="h-4 w-4" /> Net Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">{fmt(allTime.total_net_revenue)}</div>
            <p className="text-xs text-muted-foreground">After tax separation</p>
          </CardContent>
        </Card>

        <Card data-testid="current-month-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <FileText className="h-4 w-4" /> This Month
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{fmt(currentMonth.fiat_revenue)}</div>
            <p className="text-xs text-muted-foreground">
              {currentMonth.payments || 0} payments | Tax: {fmt(currentMonth.tax_collected)}
            </p>
          </CardContent>
        </Card>
      </div>

      <Separator />

      {/* Month Selector + Settlement */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Period:</span>
          <Select value={selectedMonth} onValueChange={(v) => { setSelectedMonth(v); setSettlement(null); setSeal(null); setVerification(null); }}>
            <SelectTrigger className="w-[180px]" data-testid="month-selector">
              <SelectValue placeholder="Select month" />
            </SelectTrigger>
            <SelectContent>
              {availableMonths.map(m => (
                <SelectItem key={m} value={m}>{m}</SelectItem>
              ))}
              {availableMonths.length === 0 && (
                <SelectItem value="none" disabled>No data yet</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
        {selectedMonth && (
          <Button
            variant="default"
            size="sm"
            onClick={() => loadSettlement(selectedMonth)}
            disabled={settlementLoading}
            data-testid="generate-settlement-btn"
          >
            <ArrowDownToLine className="h-4 w-4 mr-1" />
            {settlementLoading ? 'Generating...' : 'Generate Settlement Report'}
          </Button>
        )}
      </div>

      {/* Settlement Report */}
      {settlement && (
        <Card className="border-primary/20" data-testid="settlement-report">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Tax Settlement Report — {settlement.period}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Total Bookings</p>
                <p className="text-lg font-bold">{settlement.total_bookings}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Gross Revenue (Locked USD)</p>
                <p className="text-lg font-bold">{fmt(settlement.total_fiat_revenue)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Tax Collected</p>
                <p className="text-lg font-bold text-orange-600 dark:text-orange-400">{fmt(settlement.total_tax_collected)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Net Revenue</p>
                <p className="text-lg font-bold text-green-600 dark:text-green-400">{fmt(settlement.total_net_revenue)}</p>
              </div>
            </div>

            {/* By Asset */}
            {Object.keys(settlement.by_asset || {}).length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">By Crypto Asset</p>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(settlement.by_asset).map(([asset, data]) => (
                    <Badge key={asset} variant="outline" className="px-3 py-1.5 text-xs">
                      {asset.toUpperCase()}: {data.count} payments — {data.crypto_total} {asset.toUpperCase()} = {fmt(data.fiat_total)}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* By Tax Jurisdiction */}
            {Object.keys(settlement.by_tax_jurisdiction || {}).length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Tax Breakdown by Jurisdiction</p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Jurisdiction</TableHead>
                      <TableHead>Tax Type</TableHead>
                      <TableHead>Rate</TableHead>
                      <TableHead>Taxable Amount</TableHead>
                      <TableHead>Tax Due</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(settlement.by_tax_jurisdiction).map(([jur, data]) => (
                      <TableRow key={jur}>
                        <TableCell className="font-medium">{jur}</TableCell>
                        <TableCell>{data.tax_label}</TableCell>
                        <TableCell>{fmtPct(data.tax_rate)}</TableCell>
                        <TableCell>{fmt(data.taxable_amount)}</TableCell>
                        <TableCell className="font-bold text-orange-600 dark:text-orange-400">{fmt(data.tax_collected)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* On-Chain Seal Section */}
      {settlement && settlement.total_bookings > 0 && (
        <Card className={seal?.on_chain ? "border-green-500/30 bg-green-500/5" : "border-dashed"} data-testid="seal-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Link2 className="h-5 w-5" />
              On-Chain Settlement Seal
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!seal ? (
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="text-sm text-muted-foreground">
                  Seal this settlement report on Base Sepolia. The SHA-256 hash of the report data gets stored permanently on-chain
                  as a tamper-proof notary stamp. One transaction per month.
                </div>
                <Button
                  onClick={handleSealOnChain}
                  disabled={sealLoading}
                  className="shrink-0"
                  data-testid="seal-on-chain-btn"
                >
                  <Link2 className="h-4 w-4 mr-1" />
                  {sealLoading ? 'Sealing...' : 'Seal on Base Sepolia'}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  {verification?.verified ? (
                    <Badge className="bg-green-600 text-white gap-1">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Verified — Data matches on-chain hash
                    </Badge>
                  ) : verification?.tampered ? (
                    <Badge variant="destructive" className="gap-1">
                      <AlertTriangle className="h-3.5 w-3.5" /> WARNING — Data modified since seal
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="gap-1">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Sealed
                    </Badge>
                  )}
                  {seal.on_chain && <Badge variant="outline">On-Chain</Badge>}
                  {!seal.on_chain && <Badge variant="secondary">DB Only</Badge>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">SHA-256 Hash:</span>
                    <code className="block mt-0.5 text-xs font-mono bg-muted px-2 py-1 rounded break-all" data-testid="settlement-hash">
                      {seal.settlement_hash}
                    </code>
                  </div>
                  {seal.tx_hash && (
                    <div>
                      <span className="text-muted-foreground">Transaction:</span>
                      <div className="flex items-center gap-1 mt-0.5">
                        <code className="text-xs font-mono bg-muted px-2 py-1 rounded">
                          {seal.tx_hash.slice(0, 18)}...{seal.tx_hash.slice(-8)}
                        </code>
                        {seal.explorer_url && (
                          <a href={seal.explorer_url} target="_blank" rel="noopener noreferrer"
                             className="text-primary hover:underline" data-testid="explorer-link">
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                  {seal.block_number && (
                    <div>
                      <span className="text-muted-foreground">Block:</span>
                      <span className="ml-1 font-mono text-xs">{seal.block_number.toLocaleString()}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Sealed:</span>
                    <span className="ml-1 text-xs">{new Date(seal.sealed_at).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Ledger Entries Table */}
      <Card data-testid="ledger-entries-card">
        <CardHeader>
          <CardTitle className="text-base">Payment Ledger {selectedMonth && `— ${selectedMonth}`}</CardTitle>
        </CardHeader>
        <CardContent>
          {entries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground" data-testid="no-entries">
              <Lock className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p>No crypto payments recorded for this period.</p>
              <p className="text-xs mt-1">Payments will appear here automatically when guests pay with crypto.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Guest</TableHead>
                    <TableHead>Crypto Paid</TableHead>
                    <TableHead>Rate</TableHead>
                    <TableHead>Locked USD</TableHead>
                    <TableHead>Tax</TableHead>
                    <TableHead>Net</TableHead>
                    <TableHead>Tx</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((e) => (
                    <TableRow key={e.id} data-testid={`ledger-entry-${e.id}`}>
                      <TableCell className="text-xs whitespace-nowrap">
                        {new Date(e.locked_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-sm">
                        <div>{e.guest_name || '—'}</div>
                        {e.room_name && <div className="text-xs text-muted-foreground">{e.room_name}</div>}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {e.crypto_amount?.toFixed(6)} {e.crypto_asset}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        ${e.exchange_rate?.toLocaleString()}/{e.crypto_asset}
                      </TableCell>
                      <TableCell className="font-bold">{fmt(e.fiat_value_locked)}</TableCell>
                      <TableCell className="text-orange-600 dark:text-orange-400">{fmt(e.tax_amount)}</TableCell>
                      <TableCell className="text-green-600 dark:text-green-400">{fmt(e.net_revenue)}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {e.tx_hash ? `${e.tx_hash.slice(0, 8)}...` : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
